import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, MapPin, TrendingUp } from "lucide-react";
import { Header } from "@/components/Header";
import { KnowledgeConnections } from "@/components/knowledge/KnowledgeConnections";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/BreadcrumbNav";
import { JsonLd } from "@/components/JsonLd";
import { generateCanonicalUrl } from "@/utils/seoHelpers";

const SEOConroePage = () => {
  const [language, setLanguage] = useState<'es' | 'en'>('es');
  const toggleLanguage = () => setLanguage(language === 'es' ? 'en' : 'es');
  const canonicalUrl = generateCanonicalUrl('/seo-conroe');

  const content = {
    es: {
      title: "SEO Conroe TX | Posicionamiento Local en Google – Luis ROC",
      description: "Agencia SEO en Conroe, Texas. Mejora tu visibilidad en Google Maps y búsquedas locales. Optimización para ZIP 77301-77306. Reunión inicial gratuita con un especialista.",
      h1: "SEO en Conroe TX — Mejora tu visibilidad en Google Maps y búsquedas locales",
      intro: "Conroe, sede del condado de Montgomery con más de 98,000 habitantes, es un mercado en crecimiento constante. En Luis ROC optimizamos tu presencia digital para mejorar la visibilidad de tu negocio cuando residentes de Conroe buscan tus servicios. Cobertura completa: ZIP 77301, 77302, 77303, 77304, 77305, 77306.",
      keywords: "seo conroe tx, agencia seo conroe texas, posicionamiento google conroe, seo local conroe"
    },
    en: {
      title: "SEO Conroe TX | Local Google Ranking – Luis ROC",
      description: "SEO agency in Conroe, Texas. Improve your visibility on Google Maps and local searches. Optimization for ZIP 77301-77306. free 30-minute initial consultation with an SEO specialist.",
      h1: "SEO in Conroe TX — Improve your visibility on Google Maps and local searches",
      intro: "Conroe, Montgomery County seat with over 98,000 residents, is a constantly growing market. At Luis ROC we optimize your digital presence to improve your business visibility when Conroe residents search for relevant services. Full coverage: ZIP 77301, 77302, 77303, 77304, 77305, 77306.",
      keywords: "seo conroe tx, seo agency conroe texas, google ranking conroe, local seo conroe"
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": language === 'es' ? "SEO en Conroe TX" : "SEO in Conroe TX",
    "provider": { "@id": "https://luisroc.com/#organization" },
    "areaServed": {
      "@type": "City",
      "name": "Conroe",
      "containedIn": {
        "@type": "State",
        "name": "Texas"
      }
    },
    "description": content[language].description
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://luisroc.com" },
      { "@type": "ListItem", "position": 2, "name": "Servicios", "item": "https://luisroc.com/servicios" },
      { "@type": "ListItem", "position": 3, "name": "SEO Conroe", "item": canonicalUrl }
    ]
  };

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{content[language].title}</title>
        <meta name="description" content={content[language].description} />
        <meta name="keywords" content={content[language].keywords} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Hreflang tags */}
        
        {/* Geo tags */}
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Conroe" />
        <meta name="geo.position" content="30.3119;-95.4560" />
        
        {/* Open Graph */}
        <meta property="og:title" content={content[language].title} />
        <meta property="og:description" content={content[language].description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:locale" content={language === 'es' ? 'es_US' : 'en_US'} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={content[language].title} />
        <meta name="twitter:description" content={content[language].description} />
      </Helmet>

      <JsonLd data={serviceSchema} />
      <JsonLd data={breadcrumbSchema} />

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={toggleLanguage} />
        
        <main className="container mx-auto px-4 py-8">
          <BreadcrumbNav 
            items={[
              { name: language === 'es' ? "Servicios" : "Services", url: "/servicios" },
              { name: language === 'es' ? "Localidades" : "Locations", url: "/servicios-locales-houston" },
              { name: "SEO Conroe", url: "/seo-conroe" }
            ]}
            language={language}
          />

          <section className="py-16 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
              {content[language].h1}
            </h1>
            
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
              {content[language].intro}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={() => window.location.href = '/diagnostico-seo-gratis'}>
                {language === 'es' ? 'Reunión inicial gratuita de 30 minutos' : 'Free 30-Minute Initial Consultation'}
              </Button>
              <Button size="lg" variant="outline" onClick={() => window.location.href = '/contacto'}>
                {language === 'es' ? 'Hablar con Experto' : 'Talk to Expert'}
              </Button>
            </div>
          </section>

          <section className="py-16 bg-accent/5 rounded-lg mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">
              {language === 'es' ? '¿Por qué SEO local en Conroe?' : 'Why local SEO in Conroe?'}
            </h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <MapPin className="h-8 w-8 text-primary mb-4" />
                <h3 className="font-semibold text-xl mb-3">
                  {language === 'es' ? 'Mercado en crecimiento' : 'Growing market'}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'es' 
                    ? 'Conroe crece 3% anual. Posicionarte ahora te da ventaja competitiva sostenible.'
                    : 'Conroe grows 3% annually. Position yourself now for sustainable competitive advantage.'}
                </p>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <Check className="h-8 w-8 text-primary mb-4" />
                <h3 className="font-semibold text-xl mb-3">
                  {language === 'es' ? 'Google Maps crucial' : 'Google Maps crucial'}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'es'
                    ? 'En consultas locales, el map pack concentra buena parte de los clics: la ficha y las páginas de servicio deben estar alineadas.'
                    : 'In local queries the map pack takes a large share of clicks: your profile and service pages need to line up.'}
                </p>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <TrendingUp className="h-8 w-8 text-primary mb-4" />
                <h3 className="font-semibold text-xl mb-3">
                  {language === 'es' ? 'ROI sostenible' : 'Sustainable ROI'}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'es'
                    ? 'SEO local genera leads orgánicos continuos sin pagar por clic.'
                    : 'Local SEO generates continuous organic leads without paying per click.'}
                </p>
              </Card>
            </div>
          </section>

          <section className="py-16 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">
              {language === 'es' ? 'Mejora tu visibilidad en Google en Conroe TX' : 'Improve your Google visibility in Conroe TX'}
            </h2>
            <p className="text-xl mb-6 opacity-90">
              {language === 'es' ? 'Reunión inicial gratuita de 30 minutos para definir siguientes pasos' : 'Free 30-minute initial consultation to define next steps'}
            </p>
            <Button className="whitespace-normal h-auto py-3 text-center" size="lg" variant="secondary" onClick={() => window.location.href = '/diagnostico-seo-gratis'}>
              {language === 'es' ? 'Agendar reunión inicial gratuita de 30 minutos' : 'Book a free 30-minute initial consultation'}
            </Button>
          </section>

          <section className="py-8 text-center">
            <h3 className="text-xl font-semibold mb-4">
              {language === 'es' ? 'Servicios en otras localidades' : 'Services in other locations'}
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="outline" onClick={() => window.location.href = '/seo-the-woodlands'}>SEO The Woodlands</Button>
              <Button variant="outline" onClick={() => window.location.href = '/seo-houston'}>SEO Houston</Button>
              <Button variant="outline" onClick={() => window.location.href = '/seo-spring'}>SEO Spring</Button>
              <Button variant="outline" onClick={() => window.location.href = '/servicios-locales-houston'}>
                {language === 'es' ? 'Ver Todas' : 'View All'}
              </Button>
            </div>
          </section>
        </main>

        <KnowledgeConnections path="/seo-conroe" language={language} />

        <Footer language={language} />
      </div>
    </>
  );
};

export default SEOConroePage;
